import { IntlShape } from 'react-intl';

import { AddOn } from 'store/resource/types';
import { formatMonthDay } from 'utils/formatDate';

/*
 * Largest whole unit that divides the window evenly: 86400 reads "1 day", 5400 reads "90 minutes".
 * Mirrors the Service Request formatter, so one product is not worded differently on the two surfaces.
 */
const NOTICE_UNITS: Array<{ unit: 'day' | 'hour' | 'minute'; seconds: number }> = [
  { unit: 'day', seconds: 86400 },
  { unit: 'hour', seconds: 3600 },
  { unit: 'minute', seconds: 60 },
];

export const formatNoticePeriod = (intl: IntlShape, seconds: number): string => {
  const match = NOTICE_UNITS.find(candidate => seconds % candidate.seconds === 0) ?? NOTICE_UNITS[2];

  return intl.formatNumber(Math.ceil(seconds / match.seconds), {
    style: 'unit',
    unit: match.unit,
    unitDisplay: 'long',
  });
};

interface NoticeContext {
  intl: IntlShape;
  locale: string;
  /* The booking's start. Always present on this step -- a booking cannot exist without one. */
  bookingStart?: string;
  /* The BUILDING's zone, not the device's: a 20:00 EDT booking is the next day in UTC. */
  timeZone?: string;
  /*
   * RES-1398. The booking already carries this product, so the line is kept rather than dropped and
   * the out-of-window sentence would be false under it. Derived in the hook from the booking's own
   * rows -- never from `quantity`, which reads the raw slice and is also non-zero for a line that
   * was chosen while orderable and has since been flagged.
   */
  isKept?: boolean;
}

/*
 * The line beneath an add-on row, or null when there is nothing to say.
 *
 * The reason is read before the lead time: a price-unavailable product carries no lead time, so a
 * seconds guard ahead of it returns null and the row greys with no explanation at all.
 */
export const addOnMessage = (
  addOn: AddOn,
  { intl, locale, bookingStart, timeZone, isKept }: NoticeContext,
): string | null => {
  if (addOn.unavailable_reason === 'price_unavailable') {
    return intl.formatMessage({ id: 'add_on_price_unavailable' });
  }

  const seconds = addOn.minimum_notice_seconds;

  if (!seconds) {
    return null;
  }

  const notice = formatNoticePeriod(intl, seconds);

  if (!addOn.unavailable_reason) {
    return intl.formatMessage({ id: 'add_on_notice_info' }, { notice });
  }

  if (isKept) {
    return intl.formatMessage({ id: 'add_on_notice_kept' }, { notice });
  }

  return bookingStart
    ? intl.formatMessage(
        { id: 'add_on_notice_unavailable' },
        { date: formatMonthDay(bookingStart, locale, timeZone), notice },
      )
    : intl.formatMessage({ id: 'add_on_notice_info' }, { notice });
};
